// Progressive media playback over the WebRTC "xfer" channel (item: video
// streaming). Instead of buffering an entire video/audio file into a Blob
// before it can play, we feed the ordered byte stream straight into a
// MediaSource SourceBuffer so the <video>/<audio> element starts playing while
// later chunks are still arriving — true streaming, not download-then-play.
//
// The xfer channel is strictly ordered (SCTP ordered mode), which is exactly
// what a MediaSource needs. Fragmented MP4 (moov-at-front / "faststart") and
// WebM play progressively; a moov-at-end MP4 still works — it just becomes
// playable once endOfStream() fires and enough is buffered.
//
// If the browser can't create a SourceBuffer for the container (unknown codecs,
// no MSE), createStreamSink returns null and the caller falls back to the
// original full-download-then-preview path. Nothing regresses for images, PDFs,
// text, or encrypted (.hcloud) files — those never take this path.

/** Candidate SourceBuffer codec strings per container, most-specific first. */
function codecCandidates(mime: string): string[] {
  const base = mime.split(';')[0].trim().toLowerCase();
  switch (base) {
    case 'video/mp4':
      return [
        'video/mp4; codecs="avc1.42E01E, mp4a.40.2"',
        'video/mp4; codecs="avc1.640028, mp4a.40.2"',
        'video/mp4; codecs="mp4a.40.2"',
        'video/mp4',
      ];
    case 'video/webm':
      return [
        'video/webm; codecs="vp8, vorbis"',
        'video/webm; codecs="vp9, opus"',
        'video/webm',
      ];
    case 'audio/mp4':
    case 'audio/aac':
      return ['audio/mp4; codecs="mp4a.40.2"', 'audio/mp4'];
    case 'audio/mpeg':
      return ['audio/mpeg'];
    case 'audio/ogg':
      return ['audio/ogg; codecs="vorbis"', 'audio/ogg'];
    case 'audio/wav':
    case 'audio/x-wav':
      return ['audio/wav'];
    default:
      // Other video/* or audio/* containers: try the bare type last.
      if (base.startsWith('video/') || base.startsWith('audio/')) return [base];
      return [];
  }
}

/** True when the mime is a streamable media container on this browser. */
export function canStream(mime: string): boolean {
  if (typeof MediaSource === 'undefined') return false;
  return codecCandidates(mime).some((c) => MediaSource.isTypeSupported(c));
}

/** A live MediaSource sink: append ordered chunks, then end. */
export interface StreamSink {
  /** Object URL to hand to a <video>/<audio> src. */
  readonly url: string;
  /** The codec string actually used for the SourceBuffer. */
  readonly mime: string;
  /** Queue + append one ordered chunk. */
  append(chunk: ArrayBuffer): void;
  /** Signal end-of-stream once the final chunk is appended. */
  end(): void;
  /** Release the object URL. */
  close(): void;
}

/**
 * Create a MediaSource-backed sink for the given container mime, or null when
 * the browser can't stream it (caller should fall back to full download).
 */
export function createStreamSink(mime: string): StreamSink | null {
  if (typeof MediaSource === 'undefined') return null;
  const codec = codecCandidates(mime).find((c) => MediaSource.isTypeSupported(c));
  if (!codec) return null;

  const ms = new MediaSource();
  const url = URL.createObjectURL(ms);
  let sb: SourceBuffer | null = null;
  let failed = false;
  const queue: ArrayBuffer[] = [];
  let ended = false;
  let closed = false;

  const flush = () => {
    if (!sb || sb.updating || failed) return;
    if (queue.length === 0) {
      if (ended && ms.readyState === 'open') {
        try {
          ms.endOfStream();
        } catch {
          /* already ended */
        }
      }
      return;
    }
    const next = queue.shift();
    if (next) {
      try {
        sb.appendBuffer(next);
      } catch {
        failed = true;
      }
    }
  };

  ms.addEventListener('sourceopen', () => {
    try {
      sb = ms.addSourceBuffer(codec);
      sb.addEventListener('updateend', flush);
      flush();
    } catch {
      failed = true;
    }
  });

  return {
    url,
    mime: codec,
    append(chunk) {
      if (failed || closed) return;
      queue.push(chunk);
      flush();
    },
    end() {
      ended = true;
      flush();
    },
    close() {
      if (closed) return;
      closed = true;
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    },
  };
}
